import { ChevronRight } from "lucide-react";
import { cn } from "@wa/ui";
import { useT } from "../../../../i18n/I18nProvider";
import { Avatar, fmtStamp } from "../../../ui";
import type { RowMenuProps } from "../../../shell/ContextMenu";
import { useWorkCompact } from "../../_shared/density";
import { summarizeMailGroup, type MailGroup } from "./group";

/**
 * The folded line that stands for every conversation with one correspondent.
 *
 * Built out of the same vocabulary as `ThreadRow` on purpose - the same avatar,
 * the same name-and-time top line, the same subject line underneath - because
 * it sits in the same column as ordinary rows and a second row shape would read
 * as a second kind of thing.
 *
 * IT KEEPS THE AVATAR rather than swapping in a count tile the way the call
 * log's folded line does. The initials disc is what says who a mail row is
 * from, a column of them is how the folder is scanned, and a grey square in the
 * middle of that column would break the only rhythm the list has. The counts go
 * on the line underneath, where the subject would be.
 *
 * THE NAME COMES FROM `summarizeMailGroup`, not from `threadWho`. The row rule
 * falls through to the sender's own name, which on a conversation we started is
 * ours; a folded line headed by our own mailbox would name the wrong end of
 * every conversation under it. See `groupWho`.
 *
 * THE UNREAD COUNT IS THE ONE ALARM LEFT ON, and it is drawn twice on purpose:
 * as a number, and as the bold `is-unread` weight the flat rows already use.
 * Folding puts the unread rows out of sight, which would cost an operator
 * exactly the rows they open the folder for.
 */
export function MailGroupRow({
  group,
  open,
  onToggle,
  /** True while one of the conversations inside is the one the pane holds. */
  active,
  menuProps,
}: {
  group: MailGroup;
  open: boolean;
  onToggle: () => void;
  active: boolean;
  /** Right-click and long-press: the list's menu, acting on the whole group. */
  menuProps?: RowMenuProps;
}) {
  const t = useT();
  const compact = useWorkCompact();
  const { latest, unread, messages, who } = summarizeMailGroup(group);

  const count = t("mail.group.count", { count: String(group.rows.length) });
  const meta = t("mail.group.messages", { count: String(messages) });

  return (
    <button
      type="button"
      className={cn("crm-item crm-group-h", active && !open && "is-on", unread > 0 && "is-unread")}
      aria-expanded={open}
      onClick={onToggle}
      title={open ? t("mail.group.collapse") : t("mail.group.expand")}
      {...menuProps}
    >
      <ChevronRight className={cn("crm-group-chev size-4", open && "is-open")} strokeWidth={2.5} />
      <Avatar name={who} size={compact ? 28 : 38} />

      <span className="min-w-0 flex-1">
        <span className="crm-item-top">
          <span className="crm-item-from">{who}</span>
          <span className="crm-item-time">{fmtStamp(latest.lastMessageAt)}</span>
        </span>

        <span className="crm-item-sub flex items-center gap-1.5">
          <span className="min-w-0 flex-1 truncate">
            {count}
            {/* The message count is the second half of the same sentence, so it
                sits behind the same middle dot the compact row uses for its
                preview rather than in a chip of its own. */}
            <span className="crm-item-tail">{meta}</span>
          </span>
          {unread ? (
            <span className="crm-chip flex-none" title={t("mail.group.unreadHint")}>
              {unread}
            </span>
          ) : null}
        </span>
      </span>
    </button>
  );
}
